/**
 * 造型间（自由出行批 2026-10-11，茄子令由「衣帽间」扩回旧形制）：**已拥有**全类目部件的免费换装间
 * （服装/配饰/发型/五官/肤色/瞳色…全进；岁月痕迹不在类目表本就不进）。语义照 76 批造型工作室：
 * 预览免费、点「完成造型」才落 player.avatar（现在⛔ 扣钱），「离开」丢弃改动；买新件=周末商业街各店
 * （买断拥有制，价签购买流在 FreeroamMap）。
 * 编辑器=公共组件 AvatarEditor（与开局建角页/商业街部件店同源）：owned-only 过滤走 include。
 * 入口=小助理菜单「造型间」（原「造型工作室」→衣帽间→造型间）。
 */
import type { GameApp, PanelName } from '../../app';
import { GameState } from '../../systems/GameState';
import { sanitizeConfig } from '../../avatar/config';
import { FACE_CATEGORIES } from '../../avatar/categories';
import { ensureSprites } from '../../avatar/gamemount';
import { clearThumbCache } from '../../avatar/thumbs';
import { createDiv, createSpan, createButton } from '../ui';
import { AvatarEditor } from '../AvatarEditor';

export class StylingStudioPanel {
    readonly element: HTMLElement;

    private readonly editor: AvatarEditor;
    /**
     * 来时那页 ⇒「离开/完成造型」都回它（入口只有小助理菜单，而菜单在行程/人脉/微博页都点得到，⛔ 一律弹回行程页）。
     * 由 app 在切页前写入：`show()` 会先把 current 改成本面板，refresh() 里再取就取到来路了。
     */
    enteredFrom: PanelName = 'decision';

    constructor(private app: GameApp) {
        const panel = createDiv('panel-root studio-root');
        const inner = createDiv('studio-inner');

        const head = createDiv('studio-head');
        head.appendChild(createSpan('head-title', '造型间'));
        inner.appendChild(head);

        const stage = createDiv('studio-stage');
        const previewEl = createDiv('cc-preview studio-preview');
        stage.appendChild(previewEl);
        inner.appendChild(stage);

        // 底部通栏底栏（同开局页 `.cc-controls` 形制）：页签 + 部件条 + 鼻/嘴微调 + 离开/完成造型
        // （出口配方=决策页「清空 / 确认行程」同款：ghost 定宽在左 + primary 撑满在右）
        const controls = createDiv('cc-controls');
        const tabsEl = createDiv('cc-tabs');
        controls.appendChild(tabsEl);
        const stripEl = createDiv('cc-strip');
        controls.appendChild(stripEl);
        const nudgeRow = createDiv('cc-nudge-row');
        controls.appendChild(nudgeRow);
        const btnRow = createDiv('btn-row');
        btnRow.appendChild(createButton('btn btn-ghost', '离开', () => this.leave()));
        btnRow.appendChild(createButton('btn btn-primary', '完成造型', () => this.confirm()));
        controls.appendChild(btnRow);
        inner.appendChild(controls);

        panel.appendChild(inner);
        this.element = panel;

        // owned-only：没买过的件不进这一条（进货=周末商业街各店）。
        // 单例必须每次现取。全库唯一一次实例替换发生在建角页点「确认创建」时（CharacterCreatePanel 调
        //    `GameState.reset()` 换新实例），而本面板在 app 构造期就 new 好存进 panels 表。
        //    当年把 getInstance() 提到构造器顶部缓存成局部变量，闭包就此绑死旧实例、`.player` 恒 undefined，
        //    include 每渲染抛一次错，结果是**每个类目的件条全空、造型间换不了件**（10-12 C 批埋下，
        //    10-13 重钉实机桩才抓到；同族页面本来就在方法体里现取，本面板是唯一例外）。
        this.editor = new AvatarEditor({
            categories: FACE_CATEGORIES,
            preview: previewEl,
            tabs: tabsEl,
            strip: stripEl,
            nudgeRow,
            include: (cat, i) =>
                ((GameState.getInstance().player.owned?.[cat.key as 'cloth']) as number[] | undefined)?.includes(i) ?? false,
        }, sanitizeConfig(null));
    }

    /** 进面板：以当前脸起稿（owned 账本⛔ 在这里追认——只由建角确认与读档迁移写，见 GameState.ensureOwned） */
    refresh(): void {
        const state = GameState.getInstance();
        clearThumbCache();
        this.editor.setConfig(sanitizeConfig(state.player.avatar ?? null));
        this.editor.rerender();
        ensureSprites().then(() => this.editor.rerender());
    }

    destroy(): void { }

    /** 完成造型：草稿脸按 owned 过滤后落 player.avatar（免费）+ 落盘，回时那页 */
    private confirm(): void {
        const state = GameState.getInstance();
        // 本面板 owned-only，条里本就没有未买件；联动顶槽（刘海「无」顶侧发）由 wearDraft 放行并入 owned
        state.wearDraft(this.editor.cfg);
        state.warmAvatarCache();
        // 不落盘（月存口径 2026-11-03）：造型完成等月初那一格捎带
        this.app.show(this.enteredFrom);
    }

    /** 离开：丢弃草稿（改动未落档），直接回时那页 */
    private leave(): void {
        this.app.show(this.enteredFrom);
    }
}
